import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
// The goals the client form's Goal field picks from are the same list a
// curated service is made from — one endpoint, one mapping, read from where it
// already lives rather than copied into this feature.
import { fetchGoals } from "../../clients/services/catalogService";
import {
  createService,
  fetchServices,
  setServiceActive,
  updateService,
} from "../services/servicesService";
import type { BrandService, NewBrandService, ServiceGoal } from "../types/brand.types";

export type ServicesStatus = "loading" | "ready" | "failed";

export interface ServicesState {
  /** Every active service the workspace has, as the API returned them. */
  services: BrandService[];
  status: ServicesStatus;
  /** What a failed state's retry calls — both lists, since both may be down. */
  reload: () => void;
  /**
   * The goals the create dialog offers. Empty where that call failed, which
   * the dialog survives: a name can still be typed through "Add custom
   * service", and what it makes is a service with no goal behind it.
   */
  suggestions: ServiceGoal[];
  /**
   * True while any of the three below is with the server; whichever dialog is
   * open reads it. One flag serves all of them because only one can be open at
   * a time — the form and the confirmation are never on screen together.
   */
  saving: boolean;
  /**
   * All three answer with the record as the backend now holds it, or `null`
   * where the call failed — which has already been reported by then, so the
   * caller's only job is to keep its dialog open on a `null`.
   *
   * Each writes the saved card into the list itself rather than leaving that
   * to the caller: every one of them answers with the whole record, so there
   * is nothing a refetch would add.
   */
  create: (draft: NewBrandService) => Promise<BrandService | null>;
  /** Saves a rename or a reworded description. The goal is not editable. */
  update: (
    service: BrandService,
    draft: NewBrandService
  ) => Promise<BrandService | null>;
  /** Lists a service on the public page, or takes it off again. */
  setActive: (
    service: BrandService,
    active: boolean
  ) => Promise<BrandService | null>;
}

const EMPTY: BrandService[] = [];
const NO_GOALS: ServiceGoal[] = [];

export const SERVICES_QUERY_KEY = ["brand", "services"] as const;
export const SERVICE_GOALS_QUERY_KEY = ["brand", "service-goals"] as const;

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

/**
 * The workspace's services, as the Services tab reads them.
 *
 * Two reads behind one state: the services themselves, and the goals the
 * create dialog suggests. Separate queries because they fail separately — a
 * goals endpoint that is down is no reason to report the list as failed, and
 * the dialog is still usable without it.
 *
 * Backed by TanStack Query, like `usePlans`: a tab opened again shows the last
 * list immediately while a fresh read runs behind it, so a service added on
 * another device catches up within moments rather than making every visit wait
 * on the network.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useServices(): ServicesState {
  const getToken = useTokenGetter();
  const toast = useToast();
  const queryClient = useQueryClient();

  const services = useQuery({
    queryKey: SERVICES_QUERY_KEY,
    queryFn: ({ signal }) => fetchServices(getToken, signal),
  });

  const goals = useQuery({
    queryKey: SERVICE_GOALS_QUERY_KEY,
    // Narrowed to the rows with a numeric id, which every catalog goal has.
    // `LookupOption` allows a string one as well, because the client form's
    // Goal field picks from the services and those are identified by a
    // `public_id` — and a row this dialog could not post `goal_id` for is not
    // a suggestion it can offer.
    queryFn: ({ signal }) =>
      fetchGoals(getToken, signal).then((rows) =>
        rows.filter((row): row is ServiceGoal => typeof row.id === "number")
      ),
  });

  // Only fires when there is nothing cached to show in its place — see
  // `usePlans` for why that is what `isError` means here.
  useEffect(() => {
    if (!services.isError) return;
    console.error("services failed to load:", services.error);
    toast.error("Could not load your services", failureMessage(services.error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services.isError, services.error]);

  // Reported on its own, and quietly: the tab is still readable and the dialog
  // still opens, so this says what is missing rather than what is broken.
  useEffect(() => {
    if (!goals.isError) return;
    console.error("service goals failed to load:", goals.error);
    toast.error(
      "Could not load the suggested services",
      failureMessage(goals.error)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goals.isError, goals.error]);

  const [saving, setSaving] = useState(false);

  // Nothing to abort on unmount: a save already with the server should finish.
  // This only stops the result being written back into a gone tab.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /** The shared shape of all three: flag up, call, write back, flag down. */
  const run = useCallback(
    async (
      call: () => Promise<BrandService>,
      write: (saved: BrandService, current: BrandService[]) => BrandService[],
      failure: string
    ): Promise<BrandService | null> => {
      setSaving(true);
      try {
        const saved = await call();

        queryClient.setQueryData<BrandService[]>(
          SERVICES_QUERY_KEY,
          (current: BrandService[] | undefined) => write(saved, current ?? EMPTY)
        );

        return saved;
      } catch (error) {
        console.error(`${failure}:`, error);
        toast.error(failure, failureMessage(error));
        return null;
      } finally {
        if (mounted.current) setSaving(false);
      }
    },
    [queryClient, toast]
  );

  /** Newest first, which is where the tab reads a just-added card. */
  const prepend = (saved: BrandService, current: BrandService[]) => [
    saved,
    ...current,
  ];

  /** In place, so a saved card does not jump to the top of the grid. */
  const replace = (saved: BrandService, current: BrandService[]) =>
    current.map((service) => (service.id === saved.id ? saved : service));

  const create = useCallback(
    (draft: NewBrandService) =>
      run(
        () => createService(getToken, draft),
        prepend,
        "Could not add this service"
      ),
    [run, getToken]
  );

  const update = useCallback(
    (service: BrandService, draft: NewBrandService) =>
      run(
        () => updateService(getToken, service, draft),
        replace,
        "Could not save this service"
      ),
    [run, getToken]
  );

  const setActive = useCallback(
    (service: BrandService, active: boolean) =>
      run(
        () => setServiceActive(getToken, service, active),
        replace,
        active
          ? "Could not activate this service"
          : "Could not deactivate this service"
      ),
    [run, getToken]
  );

  const reload = useCallback(() => {
    void services.refetch();
    void goals.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services.refetch, goals.refetch]);

  return {
    services: services.data ?? EMPTY,
    status:
      services.status === "pending"
        ? "loading"
        : services.status === "error"
        ? "failed"
        : "ready",
    reload,
    suggestions: goals.data ?? NO_GOALS,
    saving,
    create,
    update,
    setActive,
  };
}

export default useServices;
